/**
 * Product Hierarchy Transformation Utilities
 *
 * These utilities handle data transformation for the product hierarchy:
 * - Parsing FSD countries from JSON strings
 * - Aggregating FSD data from FPN children to RPN level
 * - Transforming the entire product hierarchy data structure
 */

import {
  FPNDetailsHierarchy,
  ProductLineHierarchy,
  RPNDetailsHierarchy,
} from '../../../types/productLines/productHierarchy.types';
import { aggregateFSDFromChildren } from '../../../utils/fsdAggregationUtils';
import { logger } from '../../../utils/logger';
import { convertProgramPredecessorValue } from '../../../utils/programPredecessorUtils';

/**
 * Parses FSD countries from JSON string array to regular array
 * Mutates the FPN object in place for performance
 *
 * @param fpn - FPN details from backend API
 */
export const parseFPNCountries = (fpn: FPNDetailsHierarchy): void => {
  const countriesField = fpn.fsd_countries as unknown;

  if (!countriesField || typeof countriesField !== 'string') {
    fpn.fsd_countries = null;
    return;
  }

  try {
    const parsed = JSON.parse(countriesField);
    if (Array.isArray(parsed)) {
      // Filter out null values to ensure array only contains strings
      const filteredCountries = parsed.filter((country): country is string => country !== null);
      fpn.fsd_countries = filteredCountries.length > 0 ? filteredCountries : null;
    } else {
      fpn.fsd_countries = null;
    }
  } catch (error) {
    logger.warn('Failed to parse fsd_countries', { countriesField, error });
    fpn.fsd_countries = null;
  }
};

/**
 * Adds aggregated FSD data to RPN from its FPN children
 * Mutates the RPN object in place for performance
 *
 * @param rpn - RPN details from backend API
 */
export const addRPNFSDAggregate = (rpn: RPNDetailsHierarchy): void => {
  // First, parse all FPN children
  rpn.children.forEach(parseFPNCountries);

  // Then aggregate FSD data from children
  const fsdAggregate = aggregateFSDFromChildren(rpn.children);

  // Add aggregated fields to RPN
  (rpn as unknown as Record<string, unknown>).first_street_date = fsdAggregate.first_street_date;
  (rpn as unknown as Record<string, unknown>).fsd_countries = fsdAggregate.fsd_countries;
};

/**
 * Builds a map of RPN ID to RPN alias for quick lookup
 * Used for converting program_predecessor from rpn_id to rpn_alias
 *
 * @param data - Array of product lines from API
 * @returns Map of rpn_id -> rpn_alias
 */
const buildRpnIdToAliasMap = (data: ProductLineHierarchy[]): Map<number, string> => {
  const map = new Map<number, string>();

  data.forEach((productLine) => {
    productLine.children.forEach((rpn) => {
      map.set(rpn.rpn_id, rpn.rpn_alias);
    });
  });

  return map;
};

/**
 * Converts program_predecessor from RPN ID to RPN alias
 * If the value is FIRST_GENERATION_LABEL ('<1st Generation>'), leaves it unchanged
 * If the value is an RPN ID (numeric string), converts it to the corresponding RPN alias
 *
 * PERFORMANCE NOTE: This function intentionally mutates the RPN object in place for performance.
 * This is safe because:
 * - It's part of a transformation pipeline that processes fresh API data
 * - The data is not shared or referenced elsewhere during transformation
 * - The entire transformProductHierarchyData pipeline uses in-place mutations
 * - Performance is critical for large product hierarchies (1000+ RPNs)
 *
 * If you need immutable behavior for other use cases, create a new function
 * that returns a transformed copy rather than mutating.
 *
 * @param rpn - RPN details from backend API (will be mutated)
 * @param rpnIdToAliasMap - Map of rpn_id to rpn_alias for lookup
 */
export const convertProgramPredecessor = (rpn: RPNDetailsHierarchy, rpnIdToAliasMap: Map<number, string>): void => {
  rpn.program_predecessor = convertProgramPredecessorValue(rpn.program_predecessor, rpnIdToAliasMap);
};

/**
 * Transforms product hierarchy data by:
 * 1. Parsing FSD countries from JSON strings to arrays at FPN level
 * 2. Aggregating FSD data at RPN level from FPN children
 * 3. Converting program_predecessor from RPN ID to RPN alias
 * Mutates the data in place for performance
 *
 * @param data - Array of product lines from API
 * @returns The same array (mutated in place)
 */
export const transformProductHierarchyData = (data: ProductLineHierarchy[]): ProductLineHierarchy[] => {
  // Step 1: Build RPN ID to alias map for program_predecessor conversion
  const rpnIdToAliasMap = buildRpnIdToAliasMap(data);

  // Step 2: Transform each product line
  data.forEach((productLine) => {
    productLine.children.forEach((rpn) => {
      // Parse FPN countries and aggregate FSD data
      addRPNFSDAggregate(rpn);

      // Convert program_predecessor from rpn_id to rpn_alias
      convertProgramPredecessor(rpn, rpnIdToAliasMap);
    });
  });

  return data;
};
